import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import { skillInventorySchema } from "./skill.validation";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { buildPayloadWithIsoDates } from "../../utils/applicationFormPayload";

import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import {
  CheckIcon
} from "../../Icons";
import { request } from "../../utils/api";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import ApplicationFormFooter from './ApplicationFormFooter';
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import PropTypes from 'prop-types';
import CloseButton from "../../components/Application/CloseButton";


const SKILLS_PAGE_1 = [
  "IV Therapy",
  "Insertion of NGT",
  "Irrigation of Catheter",
  "Incontinence Care",
  "Knowledge of Anaphylactic Shock",
  "Knowledge of Special Diet, i.e., 1500 cal. ADA, 2gm NA+, etc.",
  "Knowledge of Disaster Preparedness",
  "Knowledge of Home Care",
  "Knowledge of OASIS",
  "Knowledge of Treatment Plan",
  "Knowledge of Title 22 and Medicare Regulation",
  "Knowledge of Laboratory Interpretation",
  "Knowledge of Autonomic Disreflexia",
  "Knowledge of Drug and Food Interactions",
  "Knowledge of Rehabilitation",
  "Knowledge of S/S of Respiratory Distress",
  "Knowledge of Dysphagia",
  "Knowledge of Aphasia",
  "Knowledge of I & O",
  "Measure VS",
  "Mouth Care",
  "Observation of Neuro Signs (PERRLA)",
  "Observation of Cardiovascular",
  "Obtaining Orders from Physician",
  "Obtaining Specimens",
  "Orthopedic Care",
  "Personal Hygiene",
  "Perform CPR",
  "Perform Heimlich Maneuver",
  "Perform Blood Glucose Monitoring",
  "Patient Teaching",
  "Post Operative Care",
  "Post Mortem Care",
  "Pulmonary Toilet",
  "Perineal Care",
  "Range of Motion",
  "Removal of Sutures",
  "Suctioning Tracheostomy Tube",
  "Soft Restraints",
  "Skin Care",
  "Sitz Bath",
  "Tracheostomy Care",
  "Transfer patient from bed to W/C and from W/C to bed",
  "Transfer patient from W/C to toilet and from toilet to W/C",
  "Transfer patient from bed to gurney and from gurney to bed",
  "Vaginal Douche",
  "Wound Care",
  "Wound Irrigation",
  "Warm Compress",
];

const SKILLS_PAGE_2 = [
  "Administration of oral medications",
  "Administration of IV medications",
  "Administration of ear drops",
  "Administration of eye drops",
  "Administration of suppositories",
  "Administration of TPN",
  "Administration of IPPB",
  "Administration of oxygen via mask",
  "Administration of oxygen via nasal cannula",
  "Administration of intramuscular injection",
  "Administration of subcutaneous injection",
  "Administration of sublingual medications",
  "Assisting patient in ambulation by using cane or walker",
  "Bed bath, sponge bath and shower",
  "Bladder care",
  "Bowel care",
  "Blood withdrawal",
  "Catheter care",
  "Catheterization",
  "Care of Central line",
  "Care of patient on Ventilator",
  "Checking for Edema",
  "Care of Decubitus Ulcer",
  "Care of Diabetic patient",
  "Care of Neurological Disorders patient (seizure)",
  "Cast care",
  "Care of patient in traction",
  "Care of patient who underwent cardiovascular bypass",
  "Care of Hemovac",
  "Care of Renal Dialysis Patient",
  "Care of COPD patient",
  "Care of Dying patient",
  "Care of Spinal Cord Injury patient",
  "Care of Head Trauma patient",
  "Care of patient in Splint",
  "Care of Blind patient",
  "Care of Stroke (CVA) patient",
  "Care of patient who wears Brace Prosthesis",
  "Care of Stump",
  "Cold Compress",
  "Continuous Suctioning via NGT",
  "Eye irrigation",
  "Ear irrigation",
  "Foot care",
  "Giving an Enema",
  "Giving NGT Feedings",
  "Giving GT Feedings",
  "Gastric irrigation (Stomach wash)",
  "Hard Restraints",
  "Insertion of Heparin Lock",
  "Insertion of Angio Catheter",
];

const ALL_SKILLS = [...SKILLS_PAGE_1, ...SKILLS_PAGE_2];

// Helper to strip nulls
const stripNulls = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(v => stripNulls(v));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const value = stripNulls(obj[key]);
      if (value !== null && value !== undefined && value !== "") {
        acc[key] = value;
      }
      if (value === false) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }
  return obj;
};

const SkillInventory = ({ onClose }) => {
  const [page, setPage] = useState(1);
  const [inventoryId, setInventoryId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");

  const methods = useForm({
    resolver: zodResolver(skillInventorySchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      applicantName: "",
      applicantSignature: "",
      applicantSignedBy: "",
      applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantStatementAccepted: false,
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      items: ALL_SKILLS.map(skill => ({
        skillName: skill,
        checkbox1: false,
        checkbox2: false,
        checkbox3: false,
        feelConfident: false,
        needTraining: false,
        nA: false,
        comments: ""
      }))
    },
    mode: "onChange"
  });

  const { handleSubmit, watch, reset, formState: { errors } } = methods;

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const payload = buildPayloadWithIsoDates(
        data,
        additionalData,
        ["applicantSignedAt", "reviewedBySignedAt"],
        { flag: actualFlag }
      );

      if (inventoryId || data.id) {
        payload.inventoryId = inventoryId || data.id;
        payload.id = inventoryId || data.id;
      }

      // Ensure applicantName is present in payload
      if (!payload.applicantName && data.applicantName) {
        payload.applicantName = data.applicantName;
      }

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      // Sanitize payload
      const sanitizedPayload = stripNulls(payload);
      let response;
      if (inventoryId || data.id) {
        response = await request(`/skill-inventories/${inventoryId || data.id}`, {
          method: 'PUT',
          body: JSON.stringify(sanitizedPayload)
        });
      } else {
        response = await request('/skill-inventories', {
          method: 'POST',
          body: JSON.stringify(sanitizedPayload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Skill Inventory processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    targetUserId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    signerName,
    handleSignatureApply,
    handleStatusAction,
    handleSave,
    isAdmin,
    isReadOnly,
    user: currentUserData,
    closeForm,
    actualFromId
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    adminNameField: "reviewedBySignedBy",
    onClose: onClose,
    onSubmit,
    currentStatus
  });

  useEffect(() => {
    const fetchSkillInventory = async () => {
      // Robust check: wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !inventoryId) return;

      try {
        if (!actualFromId && !targetUserId && !inventoryId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              applicantSignedAt: parseIsoDateToMDYUS(getLocalSystemDate()),
              items: ALL_SKILLS.map(skill => ({
                skillName: skill,
                feelConfident: false,
                needTraining: false,
                nA: false,
                comments: ""
              })),
              applicantStatementAccepted: false
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/skill-inventories/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/skill-inventories?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/skill-inventories/${actualFromId || targetUserId}`);
        }

        if (response.status === 1 && response.result?.inventory) {
          const data = response.result.inventory;
          setInventoryId(data.id);
          setCurrentStatus(data.status || "");

          const fetchedItems = data.items || [];
          const mergedItems = ALL_SKILLS.map(skill => {
            const existing = fetchedItems.find(item => item.skillName === skill);
            return existing ? {
              ...existing,
              feelConfident: !!existing.feelConfident,
              needTraining: !!existing.needTraining,
              nA: !!existing.nA
            } : {
              skillName: skill,
              feelConfident: false,
              needTraining: false,
              nA: false,
              comments: ""
            };
          });

          reset({
            ...data,
            applicantName: data.applicantName || data.employeeName || "",
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
            items: mergedItems,
            applicantStatementAccepted: data.applicantStatementAccepted || false
          });
        }
      } catch (error) {
        console.error("Error fetching skill inventory:", error);
      }
    };

    fetchSkillInventory();
  }, [targetUserId, reset, currentUserData, isAdmin, inventoryId, actualFromId]);


  const renderSkillRow = (skill, index) => {
    // Calculate global index if needed, but here we render based on array position
    // The items array maps directly to ALL_SKILLS
    const fieldIndex = ALL_SKILLS.indexOf(skill);

    return (
      <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
        <td className="border border-gray-800 p-2 text-sm">{skill}</td>
        <td className="border border-gray-800 p-2 text-center">
          <input
            type="checkbox"
            {...methods.register(`items.${fieldIndex}.feelConfident`)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
        </td>
        <td className="border border-gray-800 p-2 text-center">
          <input
            type="checkbox"
            {...methods.register(`items.${fieldIndex}.needTraining`)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
        </td>
        <td className="border border-gray-800 p-2 text-center">
          <input
            type="checkbox"
            {...methods.register(`items.${fieldIndex}.nA`)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
        </td>
        <td className="border border-gray-800 p-2">
          <input
            type="text"
            {...methods.register(`items.${fieldIndex}.comments`)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
          />
        </td>
      </tr>
    );
  };

  return (
    <div className="max-w-5xl mx-auto bg-white shadow-xl my-8">
      <CloseButton onClose={closeForm} />
      <EvaluationFormHeader title="Skills Inventory For RNs / LVNs" />
      <div className="p-8">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit((d) => onSubmit(d, 'send_to_office'))} className="space-y-6">
            <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />
            <fieldset disabled={isReadOnly} className={`border-none p-0 m-0 transition-all duration-300 ${isReadOnly ? 'opacity-60 bg-gray-50/50 grayscale-[0.2]' : ''}`}>
              <div className="mb-6">
                <EvaluationInput
                  name="applicantName"
                  label="Name of Employee:"
                  placeholder="Employee Name"
                />
              </div>

              {page === 1 && (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-800 text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-800 p-2 text-left font-bold">
                            SKILLS
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold w-24">
                            FEEL CONFIDENT
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold w-24">
                            NEED TRAINING
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold w-16">
                            N/A
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold">
                            COMMENTS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {SKILLS_PAGE_1.map((skill, index) =>
                          renderSkillRow(skill, index),
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {page === 2 && (
                <div>
                  <div className="overflow-x-auto mb-8">
                    <table className="w-full border-collapse border border-gray-800 text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-800 p-2 text-left font-bold">
                            SKILLS
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold w-24">
                            FEEL CONFIDENT
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold w-24">
                            NEED TRAINING
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold w-16">
                            N/A
                          </th>
                          <th className="border border-gray-800 p-2 text-center font-bold">
                            COMMENTS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {SKILLS_PAGE_2.map((skill, index) =>
                          renderSkillRow(skill, index),
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gray-100">
                    <div className="mb-8 bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            {...methods.register("applicantStatementAccepted")}
                            disabled={isReadOnly}
                            className={`peer h-5 w-5 appearance-none rounded-md border border-gray-300 bg-white transition-all checked:border-blue-600 checked:bg-blue-600 hover:border-blue-400 focus:ring-2 focus:ring-blue-100 ${isReadOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                              }`}
                          />
                          <CheckIcon className="pointer-events-none absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100" />
                        </div>
                        <span className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                          I verify that I have completed the Skill Inventory
                          accurately <span className="text-red-500">*</span>
                        </span>
                      </label>
                      {errors.applicantStatementAccepted && (
                        <p className="text-red-500 text-sm mt-2 ml-8 font-medium">
                          {errors.applicantStatementAccepted.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </fieldset>

            <ApplicationFormFooter
              page={page}
              setPage={setPage}
              totalPages={2}
              methods={methods}
              onSave={handleSave}
              onSubmit={(data, status) => onSubmit(data, status)}
              onStatusUpdate={handleStatusAction}
              isSignatureModalOpen={isSignatureModalOpen}
              openSignatureModal={openSignatureModal}
              closeSignatureModal={closeSignatureModal}
              handleSignatureApply={handleSignatureApply}
              signerName={signerName}
              showCertification={false}
              requireSignatures={true}
              showSignatureSections={page === 2}
              applicantName={watch("applicantName") || ""}
              applicantSignature={watch("applicantSignature") || ""}
              applicantSignedAt={watch("applicantSignedAt") || ""}
              reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
              reviewedBySignature={watch("reviewedBySignature") || ""}
              reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
              submitLabel="Submit & Print"
              isReadOnly={isReadOnly}
              onClose={closeForm}
              currentStatus={currentStatus}
            />
          </form>


        </FormProvider>
      </div>

    </div>
  );
};

export default SkillInventory;

SkillInventory.propTypes = {
  positionName: PropTypes.string
};
